import React, { useState, useEffect } from "react";
import { foldersApi } from "../api";

export default function FolderManagement() {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [timerMode, setTimerMode] = useState("none");
  const [timerEnabled, setTimerEnabled] = useState(false); // Keep for backward compatibility
  const [timerHours, setTimerHours] = useState(24);
  const [timerTargetFolderId, setTimerTargetFolderId] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const response = await foldersApi.list();
      console.log("Folders API response:", response);
      
      // The API client returns the axios response object with data property
      // The Go API structure is: { status: 200, result: [...], time: "...", message: "", errors: null }
      if (response && response.data && response.data.status === 200) {
        const foldersData = Array.isArray(response.data.result) ? response.data.result : [];
        setFolders(foldersData);
      } else {
        setFolders([]);
      }
    } catch (error) {
      showSnackbar("Error loading folders: " + (error.response?.data?.message || error.message), "error");
      console.error("Error fetching folders:", error);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (folder = null) => {
    setEditingFolder(folder);
    setFolderName(folder ? folder.name : "");
    // Handle both new timer_mode and legacy timer_enabled
    if (folder) {
      if (folder.timer_mode) {
        setTimerMode(folder.timer_mode);
        setTimerEnabled(folder.timer_mode !== "none");
      } else {
        // Legacy: use timer_enabled field
        setTimerMode(folder.timer_enabled ? "fixed" : "none");
        setTimerEnabled(folder.timer_enabled);
      }
    } else {
      setTimerMode("none");
      setTimerEnabled(false);
    }
    setTimerHours(folder ? folder.timer_hours || 24 : 24);
    setTimerTargetFolderId(folder ? folder.timer_target_folder_id || 0 : 0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFolder(null);
    setFolderName("");
    setTimerMode("none");
    setTimerEnabled(false);
    setTimerHours(24);
    setTimerTargetFolderId(0);
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) {
      showSnackbar("Folder name is required", "error");
      return;
    }

    try {
      const folderData = {
        name: folderName.trim(),
        timerMode: timerMode,
        timerEnabled: timerMode !== "none", // For backward compatibility
        timerHours: timerMode === "fixed" ? timerHours : 0,
        timerTargetFolderId: timerTargetFolderId
      };
      
      if (editingFolder) {
        // Update existing folder
        await foldersApi.put(folderData, `folders/${editingFolder.id}`);
        showSnackbar("Folder updated successfully");
      } else {
        // Create new folder
        await foldersApi.post(folderData);
        showSnackbar("Folder created successfully");
      }
      
      handleCloseDialog();
      fetchFolders();
    } catch (error) {
      const message = error.response?.data?.message || "Error saving folder";
      showSnackbar(message, "error");
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (folder.is_system_folder) {
      showSnackbar("System folders cannot be deleted", "error");
      return;
    }

    if (folder.inspection_count > 0) {
      showSnackbar("Cannot delete folder with inspections", "error");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the folder "${folder.name}"?`)) {
      return;
    }

    try {
      await foldersApi.delete(folder.id);
      showSnackbar("Folder deleted successfully");
      fetchFolders();
    } catch (error) {
      const message = error.response?.data?.message || "Error deleting folder";
      showSnackbar(message, "error");
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-min-h-96">
        <div className="tw-text-lg tw-text-gray-600">Loading folders...</div>
      </div>
    );
  }

  return (
    <div className="tw-p-6">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-flex tw-items-center">
          <svg className="tw-w-6 tw-h-6 tw-mr-2 tw-text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v2z" />
          </svg>
          Folder Management
        </h1>
        <button
          onClick={() => handleOpenDialog()}
          className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-flex tw-items-center tw-transition-colors"
        >
          <svg className="tw-w-4 tw-h-4 tw-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Folder
        </button>
      </div>

      <div className="tw-bg-white tw-shadow-sm tw-rounded-lg tw-overflow-hidden">
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-gray-50">
            <tr>
              <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Folder Name
              </th>
              <th className="tw-px-6 tw-py-3 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Inspections
              </th>
              <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Timer
              </th>
              <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Created
              </th>
              <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Last Updated
              </th>
              <th className="tw-px-6 tw-py-3 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
            {Array.isArray(folders) && folders.map((folder) => 
              <tr key={folder.id || "no-folder"} className="hover:tw-bg-gray-50">
                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                  <div className="tw-flex tw-items-center">
                    <svg className="tw-w-5 tw-h-5 tw-mr-2 tw-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v2z" />
                    </svg>
                    <span className="tw-text-sm tw-font-medium tw-text-gray-900">{folder.name}</span>
                  </div>
                </td>
                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-center">
                  <span className={`tw-inline-flex tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full ${
                    folder.inspection_count > 0 
                      ? 'tw-bg-blue-100 tw-text-blue-800' 
                      : 'tw-bg-gray-100 tw-text-gray-800'
                  }`}>
                    {folder.inspection_count}
                  </span>
                </td>
                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                  {(() => {
                    const mode = folder.timer_mode || (folder.timer_enabled && folder.timer_hours > 0 ? "fixed" : "none");
                    switch(mode) {
                    case "fixed":
                      return (
                        <div>
                          <div className="tw-text-xs tw-font-medium tw-text-blue-600">Fixed: {folder.timer_hours} hours</div>
                          <div className="tw-text-xs tw-text-gray-400">
                              → {folder.timer_target_folder_id === 0 ? "No Folder" : folders.find(f => f.id === folder.timer_target_folder_id)?.name || "Unknown"}
                          </div>
                        </div>
                      );
                    case "individual":
                      return (
                        <div>
                          <div className="tw-text-xs tw-font-medium tw-text-green-600">Individual</div>
                          <div className="tw-text-xs tw-text-gray-400">
                              → {folder.timer_target_folder_id === 0 ? "No Folder" : folders.find(f => f.id === folder.timer_target_folder_id)?.name || "Unknown"}
                          </div>
                        </div>
                      );
                    default:
                      return <span className="tw-text-gray-400">-</span>;
                    }
                  })()}
                </td>
                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                  {folder.created_at ? new Date(folder.created_at).toLocaleDateString() : "-"}
                </td>
                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                  {folder.updated_at ? new Date(folder.updated_at).toLocaleDateString() : "-"}
                </td>
                <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-center">
                  {folder.id > 0 && 
                    <div className="tw-flex tw-justify-center tw-space-x-2">
                      <button
                        onClick={() => handleOpenDialog(folder)}
                        disabled={folder.is_system_folder}
                        className={`tw-p-1 tw-rounded tw-transition-colors ${
                          folder.is_system_folder
                            ? 'tw-text-gray-400 tw-cursor-not-allowed'
                            : 'tw-text-blue-600 hover:tw-text-blue-800'
                        }`}
                        title={folder.is_system_folder ? "System folders cannot be edited" : "Edit folder"}
                      >
                        <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteFolder(folder)}
                        disabled={folder.is_system_folder || folder.inspection_count > 0}
                        className={`tw-p-1 tw-rounded tw-transition-colors ${
                          folder.is_system_folder || folder.inspection_count > 0
                            ? 'tw-text-gray-400 tw-cursor-not-allowed'
                            : 'tw-text-red-600 hover:tw-text-red-800'
                        }`}
                        title={
                          folder.is_system_folder 
                            ? "System folders cannot be deleted"
                            : folder.inspection_count > 0 
                              ? "Cannot delete folder with inspections" 
                              : "Delete folder"
                        }
                      >
                        <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  }
                </td>
              </tr>
            )}
            {(!Array.isArray(folders) || folders.length === 0) && 
              <tr>
                <td colSpan={6} className="tw-px-6 tw-py-8 tw-text-center">
                  <div className="tw-text-gray-500">
                    <svg className="tw-w-12 tw-h-12 tw-mx-auto tw-mb-2 tw-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v2z" />
                    </svg>
                    <p className="tw-text-sm">No folders found. Create your first folder to get started.</p>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && 
        <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50 tw-overflow-y-auto tw-h-full tw-w-full tw-z-50">
          <div className="tw-relative tw-top-20 tw-mx-auto tw-p-5 tw-border tw-w-96 tw-shadow-lg tw-rounded-md tw-bg-white">
            <div className="tw-mt-3">
              <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4">
                {editingFolder ? "Edit Folder" : "Create New Folder"}
              </h3>
              <div className="tw-mb-4">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-blue-500 focus:tw-border-blue-500"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSaveFolder();
                    }
                  }}
                  autoFocus
                  placeholder="Enter folder name"
                />
              </div>
              
              {/* Timer Settings */}
              <div className="tw-mb-4 tw-border-t tw-pt-4">
                <h3 className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">Timer Mode</h3>
                
                <div className="tw-space-y-2 tw-mb-3">
                  <label className="tw-flex tw-items-center">
                    <input
                      type="radio"
                      name="timerMode"
                      value="none"
                      className="tw-h-4 tw-w-4 tw-text-blue-600 focus:tw-ring-blue-500 tw-border-gray-300"
                      checked={timerMode === "none"}
                      onChange={(e) => setTimerMode(e.target.value)}
                    />
                    <span className="tw-ml-2 tw-text-sm tw-text-gray-700">No timer</span>
                  </label>
                  
                  <label className="tw-flex tw-items-center">
                    <input
                      type="radio"
                      name="timerMode"
                      value="fixed"
                      className="tw-h-4 tw-w-4 tw-text-blue-600 focus:tw-ring-blue-500 tw-border-gray-300"
                      checked={timerMode === "fixed"}
                      onChange={(e) => setTimerMode(e.target.value)}
                    />
                    <span className="tw-ml-2 tw-text-sm tw-text-gray-700">Fixed timer (all inspections move after same duration)</span>
                  </label>
                  
                  <label className="tw-flex tw-items-center">
                    <input
                      type="radio"
                      name="timerMode"
                      value="individual"
                      className="tw-h-4 tw-w-4 tw-text-blue-600 focus:tw-ring-blue-500 tw-border-gray-300"
                      checked={timerMode === "individual"}
                      onChange={(e) => setTimerMode(e.target.value)}
                    />
                    <span className="tw-ml-2 tw-text-sm tw-text-gray-700">Individual timers (set per inspection)</span>
                  </label>
                </div>
                
                {timerMode === "fixed" && 
                  <div className="tw-mb-3">
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Hours until move (1-168)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-blue-500 focus:tw-border-blue-500"
                      value={timerHours}
                      onChange={(e) => setTimerHours(parseInt(e.target.value) || 24)}
                    />
                  </div>
                }
                
                {(timerMode === "fixed" || timerMode === "individual") && 
                  <div className="tw-mb-3">
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Move to folder
                    </label>
                    <select
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-blue-500 focus:tw-border-blue-500"
                      value={timerTargetFolderId}
                      onChange={(e) => setTimerTargetFolderId(parseInt(e.target.value))}
                    >
                      <option value="0">No Folder</option>
                      {folders.filter(f => f.id !== editingFolder?.id).map((f) => 
                        <option key={f.id} value={f.id}>{f.name}</option>
                      )}
                    </select>
                  </div>
                }
                
                {timerMode === "individual" && 
                  <div className="tw-p-3 tw-bg-blue-50 tw-rounded-md">
                    <p className="tw-text-sm tw-text-blue-800">
                      Individual timers are set per inspection. After saving this folder, 
                      you can set specific move times for each inspection in this folder.
                    </p>
                  </div>
                }
              </div>
              
              <div className="tw-flex tw-justify-end tw-space-x-3">
                <button
                  onClick={handleCloseDialog}
                  className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-200 tw-transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFolder}
                  className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-border tw-border-transparent tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
                >
                  {editingFolder ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Snackbar for notifications */}
      {snackbar.open && 
        <div className={`tw-fixed tw-bottom-4 tw-left-4 tw-right-4 md:tw-left-auto md:tw-right-4 md:tw-w-96 tw-p-4 tw-rounded-md tw-shadow-lg ${
          snackbar.severity === 'error' ? 'tw-bg-red-600' : 'tw-bg-green-600'
        } tw-text-white tw-z-50`}>
          <div className="tw-flex tw-justify-between tw-items-center">
            <span>{snackbar.message}</span>
            <button
              onClick={handleCloseSnackbar}
              className="tw-ml-4 tw-text-white hover:tw-text-gray-200 tw-transition-colors"
            >
              <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      }
    </div>
  );
}